import { logger } from "../services/logService";

/** Client name: max 50 chars, allowed: letters, numbers, space, and . - ' & , ( ) */
export const CLIENT_NAME_MAX_LENGTH = 50;
export const CLIENT_NAME_PATTERN = /^[a-zA-Z0-9\s\-'&.,()]*$/;
export const CLIENT_NAME_PATTERN_DESC =
  "Only letters, numbers, spaces, and . - ' & , ( ) are allowed";

/** Client description: max 300 chars */
export const CLIENT_DESCRIPTION_MAX_LENGTH = 300;

/** Trademark: max 50 chars */
export const TRADEMARK_MAX_LENGTH = 50;

/** Web address: min 20, max 200 chars, valid URL, no spaces, no javascript:/data:, no internal IPs */
export const WEB_ADDRESS_MIN_LENGTH = 20;
export const WEB_ADDRESS_MAX_LENGTH = 200;
/** Valid URL: optional http(s)://, domain or localhost, optional port, optional path (no spaces) */
export const WEB_ADDRESS_REGEX =
  /^(https?:\/\/)?((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|localhost)(:\d{1,5})?(\/[^\s]*)?$/;
export const WEB_ADDRESS_PATTERN_DESC =
  "Enter a valid URL (e.g. https://example.com). No spaces, 20–200 characters.";

/** Billing ZIP/postal (trimmed): 3–10 alphanumeric, matches backend. */
export const BILLING_ZIP_POSTAL_CODE_PATTERN = /^[A-Za-z0-9]{3,10}$/;
export const BILLING_ZIP_CODE_ERROR =
  "ZIP/Postal code must be 3 to 10 alphanumeric characters";

/**
 * Format client_type for display: API stores TEST | Production; legacy PROD/test/production still supported.
 * @param {string} value - client_type from API
 * @returns {string} Short display label
 */
export const formatClientTypeDisplay = (value) => {
  if (value === undefined || value === null || typeof value !== "string")
    return "";
  const u = value.trim().toUpperCase();
  if (u === "TEST") return "Test";
  if (u === "PROD") return "Production";
  const lower = value.trim().toLowerCase();
  if (lower === "test") return "Test";
  if (lower === "production" || lower === "prod") return "Production";
  const s = value.trim();
  if (!s) return "";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Validate client name - returns error message or null if valid
 * @param {string} value - Client name to validate
 * @returns {string|null} Error message or null
 */
export const validateClientName = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "Client name is required";
  if (trimmed.length > CLIENT_NAME_MAX_LENGTH)
    return `Client name must be at most ${CLIENT_NAME_MAX_LENGTH} characters`;
  if (!CLIENT_NAME_PATTERN.test(trimmed)) return CLIENT_NAME_PATTERN_DESC;
  if (trimmed.length < 2) return "Client name must be at least 2 characters";
  return null;
};

/**
 * Validate client description - max 300 characters
 * @param {string} value
 * @returns {string|null} Error message or null
 */
export const validateClientDescription = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = (value || "").trim();
  if (trimmed.length > CLIENT_DESCRIPTION_MAX_LENGTH)
    return `Client description must be at most ${CLIENT_DESCRIPTION_MAX_LENGTH} characters`;
  return null;
};

/**
 * Validate trademark - max 50 characters (optional field)
 * @param {string} value
 * @returns {string|null} Error message or null
 */
export const validateTrademark = (value) => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && !value.trim())
  )
    return null;
  const trimmed = (value || "").trim();
  if (trimmed.length > TRADEMARK_MAX_LENGTH)
    return `Trademark must be at most ${TRADEMARK_MAX_LENGTH} characters`;
  return null;
};

/**
 * Validate web address URL: max 200, min 20, no spaces, no javascript:/data:, valid URL, no internal IPs
 * @param {string} value
 * @returns {string|null} Error message or null
 */
export const validateWebAddress = (value) => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && !value.trim())
  )
    return null;
  const trimmed = (value || "").trim();
  if (trimmed.length > WEB_ADDRESS_MAX_LENGTH)
    return `Web address must be at most ${WEB_ADDRESS_MAX_LENGTH} characters`;
  if (trimmed.length < WEB_ADDRESS_MIN_LENGTH)
    return `Web address must be at least ${WEB_ADDRESS_MIN_LENGTH} characters`;
  if (/\s/.test(trimmed)) return "Web address must not contain spaces";
  const lower = trimmed.toLowerCase();
  // eslint-disable-next-line no-script-url -- validating user input; not executing script
  if (lower.startsWith("javascript:") || lower.startsWith("data:"))
    return "Web address must not use javascript: or data: schemes";
  if (!WEB_ADDRESS_REGEX.test(trimmed)) return WEB_ADDRESS_PATTERN_DESC;
  try {
    const toParse = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `http://${trimmed}`;
    new URL(toParse);
  } catch (e) {
    return "Web address must be a valid URL";
  }
  return null;
};

/**
 * Validation utility for handling form validation across components
 */
class ValidationUtils {
  constructor() {
    this.rules = {};
    this.customValidators = {};
  }

  /**
   * Fetch validation rules from the server
   * @param {string} formType - Type of form to get rules for
   * @returns {Promise<Object>} Validation rules
   */
  async fetchValidationRules(formType) {
    try {
      // Since the validation rules endpoint doesn't exist in the backend,
      // use the default validation rules as fallback
      const rules = this.getDefaultValidationRules(formType);
      this.rules[formType] = rules;
      return rules;
    } catch (error) {
      logger.error(error, "Error fetching validation rules");
      // Return empty rules as final fallback
      const emptyRules = {};
      this.rules[formType] = emptyRules;
      return emptyRules;
    }
  }

  /**
   * Get default validation rules for common form types
   * @param {string} formType - Type of form
   * @returns {Object} Default validation rules
   */
  getDefaultValidationRules(formType) {
    const defaultRules = {
      "user-registration": {
        email: {
          required: true,
          pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
          patternMessage: "Please enter a valid email address",
        },
        password: {
          required: true,
          minLength: 8,
          validator: "password",
        },
        full_name: {
          required: true,
          minLength: 2,
          maxLength: 100,
        },
        role: {
          required: true,
        },
      },
      "client-form": {
        client_name: {
          required: true,
          minLength: 2,
          maxLength: 50,
          pattern: "^[a-zA-Z0-9\\s\\-'&.,()]+$",
          patternMessage: CLIENT_NAME_PATTERN_DESC,
        },
        client_description: {
          required: false,
          maxLength: CLIENT_DESCRIPTION_MAX_LENGTH,
        },
        client_trade_mark: {
          required: false,
          maxLength: TRADEMARK_MAX_LENGTH,
        },
        web_address: {
          required: false,
          minLength: WEB_ADDRESS_MIN_LENGTH,
          maxLength: WEB_ADDRESS_MAX_LENGTH,
          pattern: WEB_ADDRESS_REGEX.source,
          patternMessage: WEB_ADDRESS_PATTERN_DESC,
        },
      },
      "project-form": {
        project_name: {
          required: true,
          minLength: 2,
          maxLength: 100,
        },
        description: {
          required: false,
          maxLength: 500,
        },
        project_type: {
          required: true,
        },
        client_id: {
          required: true,
        },
      },
      password: {
        current_password: {
          required: true,
        },
        new_password: {
          required: true,
          minLength: 8,
          validator: "password",
        },
        confirm_password: {
          required: true,
        },
      },
    };

    return defaultRules[formType] || {};
  }

  /**
   * Register a custom validator function
   * @param {string} name - Validator name
   * @param {Function} validator - Validator function
   */
  registerValidator(name, validator) {
    if (typeof validator !== "function") {
      throw new Error("Validator must be a function");
    }
    this.customValidators[name] = validator;
  }

  /**
   * Validate a single field
   * @param {string} formType - Type of form
   * @param {string} fieldName - Field to validate
   * @param {any} value - Field value
   * @returns {Object} Validation result
   */
  validateField(formType, fieldName, value) {
    const rules = this.rules[formType];
    if (!rules || !rules[fieldName]) {
      logger.warn(`No validation rules found for ${fieldName} in ${formType}`);
      return { isValid: true, error: null };
    }

    const fieldRules = rules[fieldName];

    // Required check
    if (fieldRules.required && !value) {
      return {
        isValid: false,
        error: fieldRules.requiredMessage || "This field is required",
      };
    }

    // Minimum length
    if (fieldRules.minLength && value && value.length < fieldRules.minLength) {
      return {
        isValid: false,
        error: `Minimum length is ${fieldRules.minLength} characters`,
      };
    }

    // Maximum length
    if (fieldRules.maxLength && value && value.length > fieldRules.maxLength) {
      return {
        isValid: false,
        error: `Maximum length is ${fieldRules.maxLength} characters`,
      };
    }

    // Pattern matching
    if (
      fieldRules.pattern &&
      value &&
      !new RegExp(fieldRules.pattern).test(value)
    ) {
      return {
        isValid: false,
        error: fieldRules.patternMessage || "Invalid format",
      };
    }

    // Custom validator
    if (fieldRules.validator && this.customValidators[fieldRules.validator]) {
      const result = this.customValidators[fieldRules.validator](value);
      if (result && !result.isValid) {
        return result;
      }
    }

    return { isValid: true, error: null };
  }

  /**
   * Validate an entire form
   * @param {string} formType - Type of form
   * @param {Object} values - Form values
   * @returns {Object} Validation results
   */
  validateForm(formType, values) {
    const errors = {};
    let isValid = true;

    const rules = this.rules[formType];
    if (!rules) {
      logger.warn(`No validation rules found for form type: ${formType}`);
      return { isValid: true, errors: {} };
    }

    Object.keys(rules).forEach((fieldName) => {
      const result = this.validateField(formType, fieldName, values[fieldName]);
      if (!result.isValid) {
        errors[fieldName] = result.error;
        isValid = false;
      }
    });

    return { isValid, errors };
  }

  /**
   * Get validation rules for a form type
   * @param {string} formType - Type of form
   * @returns {Object|null} Validation rules
   */
  getRules(formType) {
    return this.rules[formType] || null;
  }

  /**
   * Clear cached validation rules
   * @param {string} [formType] - Specific form type to clear, or all if not specified
   */
  clearRules(formType) {
    if (formType) {
      delete this.rules[formType];
    } else {
      this.rules = {};
    }
  }
}

// Export singleton instance
export const validator = new ValidationUtils();

// Register common validators
validator.registerValidator("email", (value) => ({
  isValid: value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
  error: "Please enter a valid email address",
}));

validator.registerValidator("password", (value) => {
  if (!value) return { isValid: false, error: "Password is required" };

  const hasUpperCase = /[A-Z]/.test(value);
  const hasLowerCase = /[a-z]/.test(value);
  const hasNumbers = /\d/.test(value);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

  if (!hasUpperCase)
    return {
      isValid: false,
      error: "Password must contain at least one uppercase letter",
    };
  if (!hasLowerCase)
    return {
      isValid: false,
      error: "Password must contain at least one lowercase letter",
    };
  if (!hasNumbers)
    return {
      isValid: false,
      error: "Password must contain at least one number",
    };
  if (!hasSpecialChar)
    return {
      isValid: false,
      error: "Password must contain at least one special character",
    };

  return { isValid: true, error: null };
});

validator.registerValidator("phone", (value) => ({
  isValid: value && /^\+?[1-9]\d{1,14}$/.test(value),
  error: "Please enter a valid phone number",
}));
